'use client';

import Link from 'next/link';
import { useI18n } from 'hooks/useI18n';
import { APP_URLS, getAppUrl } from 'utils/app-urls';
import * as S from './BlogCategoryFilter.styled';
import { getPublicPath } from 'utils/paths';
import {
  faBars as faBarsLight,
  faBook as faBookLight,
  faBuilding as faBuildingLight,
  faChartLine as faChartLineLight,
  faCogs as faCogsLight,
  faCubesStacked as faCubesStackedLight,
  faFlag as faFlagLight,
  faHome as faHomeLight,
  faNetworkWired as faNetworkWiredLight,
  faPeopleGroup as faPeopleGroupLight,
  faQuestionCircle as faQuestionCircleLight,
  faTelescope as faTelescopeLight,
  IconDefinition
} from '@fortawesome/pro-light-svg-icons';
import {
  faBook as faBookSolid,
  faBuilding as faBuildingSolid,
  faChartLine as faChartLineSolid,
  faCogs as faCogsSolid,
  faCubesStacked as faCubesStackedSolid,
  faFlag as faFlagSolid,
  faNetworkWired as faNetworkWiredSolid,
  faPeopleGroup as faPeopleGroupSolid,
  faTelescope as faTelescopeSolid
} from '@fortawesome/pro-solid-svg-icons';

import { WP_REST_API_Categories } from 'wp-types';
import React from 'react';
import { Drawer, Icon } from '@asgard/ui-library';
import CategoryDrawer from './CategoryDrawer';
import SubcategoryDropdown from './SubcategoryDropdown';
import { useRouter } from 'next/router';

export const categoryIconMap: Record<
  string,
  | { type: 'icon'; iconMenu: IconDefinition; iconTag: IconDefinition }
  | { type: 'img'; imgSrc: string }
> = {
  ['Búsqueda de empleo']: {
    type: 'icon',
    iconMenu: faTelescopeLight,
    iconTag: faTelescopeSolid
  },
  ['Crecimiento laboral']: {
    type: 'icon',
    iconMenu: faChartLineLight,
    iconTag: faChartLineSolid
  },
  ['Cultura laboral']: {
    type: 'icon',
    iconMenu: faCogsLight,
    iconTag: faCogsSolid
  },
  ['Para empresas']: {
    type: 'icon',
    iconMenu: faBuildingLight,
    iconTag: faBuildingSolid
  },
  ['Equipo']: {
    type: 'icon',
    iconMenu: faPeopleGroupLight,
    iconTag: faPeopleGroupSolid
  },
  ['Manfred']: {
    type: 'img',
    imgSrc: getPublicPath('/images/blog/manfred-icon-small.svg')
  },
  ['Recursos']: {
    type: 'icon',
    iconMenu: faBookLight,
    iconTag: faBookSolid
  },
  ['Career growth']: {
    type: 'icon',
    iconMenu: faChartLineLight,
    iconTag: faChartLineSolid
  },
  ['For companies']: {
    type: 'icon',
    iconMenu: faBuildingLight,
    iconTag: faBuildingSolid
  },
  ['Job Search']: {
    type: 'icon',
    iconMenu: faTelescopeLight,
    iconTag: faTelescopeSolid
  },
  ['Work culture']: {
    type: 'icon',
    iconMenu: faCogsLight,
    iconTag: faCogsSolid
  },
  ['Resources']: {
    type: 'icon',
    iconMenu: faBookLight,
    iconTag: faBookSolid
  },
  ['Product']: {
    type: 'icon',
    iconMenu: faCubesStackedLight,
    iconTag: faCubesStackedSolid
  },
  ['Team']: {
    type: 'icon',
    iconMenu: faPeopleGroupLight,
    iconTag: faPeopleGroupSolid
  },
  ['Tech community']: {
    type: 'icon',
    iconMenu: faNetworkWiredLight,
    iconTag: faNetworkWiredSolid
  },
  ['WeAreAtWar']: {
    type: 'icon',
    iconMenu: faFlagLight,
    iconTag: faFlagSolid
  }
};

interface BlogTagFilterProps {
  category?: string;
  sortedCategories: {
    parent: WP_REST_API_Categories;
    children: WP_REST_API_Categories;
  };
}

export const BlogCategoryFilter = ({
  category,
  sortedCategories
}: BlogTagFilterProps) => {
  const { t } = useI18n('blog');
  const { locale } = useRouter();
  const [isDrawerOpen, setIsDrawerOpen] = React.useState(false);
  const [openDropdown, setOpenDropdown] = React.useState<string | null>(null);

  const parentCategoriesWithChildren = React.useMemo(() => {
    return sortedCategories.parent.map(
      (parent: WP_REST_API_Categories[number]) => ({
        ...parent,
        children: sortedCategories.children.filter(
          (cat: WP_REST_API_Categories[number]) =>
            cat.parent === parent.id && cat.count > 0
        )
      })
    );
  }, [sortedCategories]);

  const AllTab = (
    <S.StyledTabElement active={!category}>
      <S.TabLink active={!category}>
        <Icon icon={faHomeLight} size="l" />
        {t('buttons.home')}
      </S.TabLink>
    </S.StyledTabElement>
  );

  return (
    <S.CategorySection>
      <S.StyledTags>
        {category ? (
          <Link href={getAppUrl(APP_URLS.blog, locale)} passHref>
            {AllTab}
          </Link>
        ) : (
          AllTab
        )}

        {parentCategoriesWithChildren.map((cat) => {
          const isActive = category === cat.slug;
          const iconEntry = categoryIconMap[cat.name];
          const isIcon = iconEntry?.type === 'icon';
          const icon = isIcon ? iconEntry?.iconMenu : undefined;
          const img = !isIcon ? iconEntry?.imgSrc : undefined;
          const hasChildren = cat.children && cat.children.length > 0;

          return (
            <S.StyledTabElement
              key={cat.slug}
              active={isActive}
              onMouseEnter={() => {
                hasChildren && setOpenDropdown(cat.slug);
              }}
              onMouseLeave={() => hasChildren && setOpenDropdown(null)}
            >
              <Link
                href={`${getAppUrl(APP_URLS.blog, locale)}/category/${
                  cat.slug
                }`}
                passHref
              >
                <S.TabLink active={isActive}>
                  <S.CategoryIconSlot>
                    {!isIcon ? (
                      <img role="none" src={img} />
                    ) : (
                      <Icon icon={icon ?? faQuestionCircleLight} size="l" />
                    )}
                  </S.CategoryIconSlot>
                  {cat.name.charAt(0).toUpperCase() + cat.name.slice(1)}
                </S.TabLink>
              </Link>
              <SubcategoryDropdown
                locale={locale}
                isActive={isActive}
                items={cat.children}
                isOpen={openDropdown === cat.slug}
              />
            </S.StyledTabElement>
          );
        })}
      </S.StyledTags>

      <S.MobileButton
        label={t('buttons.openCategoryDrawer')}
        onClick={() => setIsDrawerOpen(true)}
        schema={'neutral'}
        startIcon={faBarsLight}
      />
      <Drawer
        isOpened={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        title="Categorías"
      >
        <CategoryDrawer
          categories={parentCategoriesWithChildren}
          onClose={() => setIsDrawerOpen(false)}
        />
      </Drawer>
    </S.CategorySection>
  );
};
